/**
 * Shared hook for getting country display data.
 * Combines static country data with API data for consistent display
 * across CountryCard and CountryInfoModal.
 */

import { useMemo } from 'react';
import { getCountryByCode } from '@/data/countries';
import { useCountryDataContext } from '@/contexts/CountryDataContext';
import type { CountryCode } from '@/data/countries/types';

export interface CountryDisplayData {
  /** The static country object */
  country: ReturnType<typeof getCountryByCode>;
  /** Country images (URLs from database) */
  images: string[];
  /** Description from API */
  description: string | null;
  /** Famous for from API */
  famousFor: string | null;
  /** Merged details (API takes precedence over static) */
  details: {
    capital?: string;
    population?: string;
    currency?: string;
    languages?: string;
    area?: string;
  };
  /** Extended API data */
  apiData: {
    hdi?: string | null;
    gdp?: string | null;
    positives?: string | null;
    negatives?: string | null;
  } | null;
}

/**
 * Parse JSON safely, returning array or wrapping string value.
 */
export function parseJsonSafe(value: string): string[] {
  try {
    return JSON.parse(value);
  } catch {
    return [value];
  }
}

/**
 * Hook to get all display data for a country.
 * Merges static data with API data for consistent display.
 */
export function useCountryDisplayData(code: CountryCode): CountryDisplayData {
  const country = useMemo(() => getCountryByCode(code), [code]);
  const { data: countryData, getPhotos } = useCountryDataContext();

  // Get photos from database, mapped to URLs
  const images = useMemo(() => {
    const dbPhotos = getPhotos(code);
    return dbPhotos.map(photo => photo.photo_url);
  }, [code, getPhotos]);

  return useMemo(() => {
    const apiData = countryData[code];

    return {
      country,
      images,
      description: apiData?.description ?? null,
      famousFor: apiData?.famous_for ?? null,
      details: {
        capital: apiData?.capital || country?.details?.capital,
        population: apiData?.population || country?.details?.population,
        currency: apiData?.currency || country?.details?.currency,
        languages: apiData?.languages || country?.details?.languages,
        area: apiData?.area || country?.details?.area,
      },
      apiData: apiData
        ? {
            hdi: apiData.hdi,
            gdp: apiData.gdp,
            positives: apiData.positives,
            negatives: apiData.negatives,
          }
        : null,
    };
  }, [code, country, images, countryData]);
}
